import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Wallet, Cpu, Landmark, Gem, Trophy, TrendingUp, Info, Gift, Users2 } from "lucide-react";

function fmt(v, d = 2) {
  return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

const LEVEL_ICONS = { silver: Gem, gold: Trophy, diamond: Landmark };

export default function Shareholder() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/shareholder/my");
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <AppLayout variant="user"><div className="text-slate-500">Loading…</div></AppLayout>;
  if (!data?.enabled) {
    return (
      <AppLayout variant="user">
        <div className="nb-card p-8 text-center">
          <Building2 size={32} className="text-slate-500 mx-auto mb-3"/>
          <h2 className="font-heading text-white text-xl">Shareholder Program temporarily paused</h2>
        </div>
      </AppLayout>
    );
  }

  const totalBalance = Number(data.total_balance || 0);
  const currentLevel = data.current_level;
  const nextLevel = data.next_level;
  const toNext = Number(data.amount_to_next_level || 0);
  const progressPct = nextLevel ? Math.min(100, (totalBalance / nextLevel.min_balance) * 100) : 100;
  const levels = data.levels || [];
  const wallet = data.wallet_breakdown || {};

  const LevelIcon = currentLevel ? LEVEL_ICONS[currentLevel.key] || Gem : Building2;
  const levelColor = currentLevel?.color || "#94A3B8";

  return (
    <AppLayout variant="user">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Company Shareholder Program</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">Earn on every business volume — in real time.</h1>
        <p className="text-slate-400 mt-2 max-w-3xl text-sm">Maintain a <strong className="text-white">Total Balance</strong> (Main Wallet + Bot Investment + Staking) above a threshold and instantly become a Silver / Gold / Diamond Shareholder. Every time <strong className="text-white">anyone in your entire network</strong> adds new business volume (deposit, bot activation or new stake) — you earn 5–15% commission on it, credited to your balance immediately.</p>
      </div>

      {/* Current status */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div data-testid="shareholder-current-level" className="nb-card p-6 md:col-span-2 relative overflow-hidden" style={{ borderColor: currentLevel ? `${levelColor}55` : undefined }}>
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${levelColor}18`, border: `1px solid ${levelColor}55` }}>
              <LevelIcon size={26} style={{ color: levelColor }}/>
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Your Shareholder Level</div>
              <div className="font-heading text-3xl font-bold mt-1" style={{ color: levelColor }} data-testid="current-level-label">
                {currentLevel ? currentLevel.label : "Not a Shareholder yet"}
              </div>
              {currentLevel && (
                <div className="mt-1 text-sm text-slate-300">
                  Earning <span className="font-mono font-bold text-white">{currentLevel.pct}%</span> on every new business volume from your network.
                </div>
              )}
              {!currentLevel && (
                <div className="mt-1 text-sm text-slate-400">
                  Reach <span className="font-mono text-white">${fmt(levels[0]?.min_balance || 2000, 0)}</span> Total Balance to unlock Silver Shareholder.
                </div>
              )}
            </div>
          </div>

          {/* Progress to next */}
          {nextLevel ? (
            <div className="mt-5">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-slate-400">Progress to <span className="text-white font-semibold">{nextLevel.label}</span></span>
                <span className="text-white font-mono">${fmt(totalBalance)} / ${fmt(nextLevel.min_balance, 0)}</span>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${levelColor}, ${nextLevel.color})` }}/>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Add <span className="text-nb-cyan font-mono">${fmt(toNext)}</span> more to unlock {nextLevel.pct}% commission.</div>
            </div>
          ) : (
            <div className="mt-5 text-xs text-[#8AE234]">🎉 You've reached the top tier — maximum commission rate active.</div>
          )}
        </div>

        <div data-testid="shareholder-commission-total" className="nb-card p-6">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Total Commission Earned</div>
          <div className="font-mono font-bold text-3xl text-[#8AE234] mt-1">${fmt(data.total_commission_earned)}</div>
          <div className="text-[11px] text-slate-500 mt-1">Lifetime · from your network's activity</div>
          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Total Balance</div>
            <div className="font-mono text-xl text-white mt-1">${fmt(totalBalance)}</div>
          </div>
        </div>
      </div>

      {/* Wallet breakdown */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <WalletCard icon={Wallet} label="Main Wallet" value={wallet.main_wallet} color="#8AE234"/>
        <WalletCard icon={Cpu} label="Bot Investment" value={wallet.bot_investment} color="#A855F7"/>
        <WalletCard icon={TrendingUp} label="Staking Balance" value={wallet.staking_balance} color="#00E5FF"/>
      </div>

      {/* All tiers */}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Shareholder Levels</div>
        <div className="grid md:grid-cols-3 gap-4" data-testid="shareholder-levels-grid">
          {levels.map((lvl) => {
            const meets = totalBalance >= lvl.min_balance;
            const isCurrent = currentLevel?.key === lvl.key;
            const Icon = LEVEL_ICONS[lvl.key] || Gem;
            return (
              <div key={lvl.key} data-testid={`shareholder-tier-${lvl.key}`} className={`rounded-2xl border p-5 relative overflow-hidden transition ${isCurrent ? "shadow-[0_0_30px_rgba(0,229,255,0.15)]" : ""} ${meets && !isCurrent ? "opacity-70" : ""}`} style={{ borderColor: isCurrent ? lvl.color : "#ffffff15", background: isCurrent ? `${lvl.color}0F` : "#0F1626" }}>
                <div className="flex items-center gap-2">
                  <Icon size={18} style={{ color: lvl.color }}/>
                  <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: lvl.color }}>{lvl.label}</div>
                  {isCurrent && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-nb-cyan/20 text-nb-cyan">ACTIVE</span>}
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-mono text-4xl font-bold" style={{ color: lvl.color }}>{lvl.pct}%</span>
                  <span className="text-slate-400 text-xs">on new business</span>
                </div>
                <div className="text-slate-400 text-sm mt-2">Total Balance ≥ <span className="font-mono text-white">${fmt(lvl.min_balance, 0)}</span></div>
                <div className="mt-4 pt-3 border-t border-white/5 text-[11px] text-slate-500 leading-relaxed">
                  Paid in real-time on every deposit, bot activation & new stake from anyone in your network (multi-level).
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info card */}
      <div className="nb-card p-5 mb-6 border-nb-cyan/20 bg-nb-cyan/5" data-testid="shareholder-rules">
        <div className="flex items-start gap-3">
          <Info size={16} className="text-nb-cyan mt-0.5 shrink-0"/>
          <div className="text-xs text-slate-300 leading-relaxed">
            <div className="font-semibold text-white mb-1">How it works</div>
            <ul className="space-y-1 list-disc pl-4 marker:text-nb-cyan/60">
              <li>Your Shareholder Level auto-upgrades or downgrades in real-time based on your Total Balance.</li>
              <li>Commission is credited on <strong className="text-white">new business volume only</strong> — no double counting on the same funds.</li>
              <li>Every upline in your chain earns their level's % on downline activity (multi-level, up to 20 levels deep).</li>
              <li>This is an <strong className="text-white">Extra Reward</strong> on top of your existing Trading, Staking, Referral and Level income.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recent commissions */}
      <div className="nb-card p-6" data-testid="shareholder-commissions-history">
        <div className="flex items-center gap-2 mb-3">
          <Gift size={16} className="text-[#EAB308]"/>
          <div className="font-heading text-white text-lg">Recent Commissions</div>
        </div>
        {(!data.recent_commissions || data.recent_commissions.length === 0) ? (
          <div className="text-sm text-slate-500 text-center py-6">
            <Users2 size={28} className="mx-auto mb-2 opacity-30"/>
            No commissions yet. Invite people or wait for your network to add new business volume.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-xs text-slate-500 uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">From</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">Business Vol.</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">Rate</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">Commission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.recent_commissions.map((c, i) => (
                  <tr key={i} className="text-slate-200">
                    <td className="px-3 py-2 text-xs text-slate-400 whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs uppercase tracking-widest text-nb-cyan">{c.meta?.source || "—"}</td>
                    <td className="px-3 py-2 text-xs">{c.meta?.downline_name || "—"} <span className="text-slate-500 text-[10px]">L{c.meta?.depth || "?"}</span></td>
                    <td className="px-3 py-2 text-right font-mono">${fmt(c.meta?.business_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono text-nb-cyan">{c.meta?.pct}%</td>
                    <td className="px-3 py-2 text-right font-mono text-[#8AE234] font-bold">+${fmt(c.amount, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function WalletCard({ icon: Icon, label, value, color }) {
  return (
    <div className="nb-card p-4 flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}40` }}>
        <Icon size={16} style={{ color }}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-mono text-white text-sm mt-0.5">${fmt(value)}</div>
      </div>
    </div>
  );
}
